import './OnboardingCard.css';
import porterLogo from '@/assets/porter-logo.png';
import { IressCheckbox, IressButton, IressIcon, IressText, IressStack } from '@iress-oss/ids-components';

export interface OnboardingPoint {
  title: string;
  description: string;
  /** Material Symbol icon name (e.g. 'search', 'home') */
  icon?: string;
}

export interface OnboardingContent {
  points: OnboardingPoint[];
  contextualTip: OnboardingPoint;
  disclaimer: string;
}

interface OnboardingCardProps {
  content: OnboardingContent;
  doNotShowAgain: boolean;
  onDoNotShowAgainChange: (checked: boolean) => void;
}

/**
 * Onboarding card shown to first-time users.
 * Explains how Porter works with three instructional points.
 * Has a "Do not show this again" checkbox.
 * Note: The "Start search" button is rendered separately in the footer.
 */
export function OnboardingCard({
  content,
  doNotShowAgain,
  onDoNotShowAgainChange,
}: Readonly<OnboardingCardProps>) {
  return (
    <div className="porter-onboarding">
      {/* Grey card containing logo, title, points, and checkbox */}
      <div className="porter-onboarding__card">
        {/* Inner content frame */}
        <IressStack gap="spacing.8" className="porter-onboarding__content">
          {/* Logo and title */}
          <IressStack gap="spacing.4" className="porter-onboarding__header">
            <div className="porter-onboarding__logo">
              <img src={porterLogo} alt="Porter" width="80" height="83" />
            </div>
            <IressText
              element="h2"
              textAlign="center"
              className="porter-onboarding__title"
            >
              How Porter works
            </IressText>
          </IressStack>

          {/* Instructional points */}
          <IressStack gap="spacing.4" className="porter-onboarding__points">
            {content.points.map((point) => (
              <IressStack key={point.title} gap="spacing.2" className="porter-onboarding__point">
                <div className="porter-onboarding__point-header">
                  {point.icon && (
                    <IressIcon
                      name={point.icon as "search"}
                      className="porter-onboarding__point-icon"
                    />
                  )}
                  <IressText element="p" className="porter-onboarding__point-title">
                    {point.title}
                  </IressText>
                </div>
                <IressText element="p" className="porter-onboarding__point-description">
                  {point.description}
                </IressText>
              </IressStack>
            ))}
          </IressStack>
        </IressStack>

        {/* Checkbox */}
        <div className="porter-onboarding__checkbox-wrapper">
          <IressCheckbox
            checked={doNotShowAgain}
            onChange={(e) => onDoNotShowAgainChange(e.target.checked)}
          >
            Do not show this again.
          </IressCheckbox>
        </div>
      </div>
    </div>
  );
}

interface StartSearchButtonProps {
  onClick: () => void;
}

/**
 * Start search button - rendered in the footer during onboarding.
 */
export function StartSearchButton({ onClick }: Readonly<StartSearchButtonProps>) {
  return (
    <div className="porter-onboarding__button-wrapper">
      <IressButton mode="primary" onClick={onClick} textStyle="typography.body.md.strong">
        Start search
        <IressIcon name="arrow_forward_ios" />
      </IressButton>
    </div>
  );
}
